/** Ethereum HEX (eHEX) parallel product. PulseChain handlers must never see these orders. */

export const ETH_CHAIN_ID = 1

export const EHEX_ADDRESS = '0x2b591e99afE9f32eAA6214f7B7629768c40Eeb39'
export const ETH_USDC_ADDRESS = '0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48'
export const ETH_USDT_ADDRESS = '0xdAC17F958D2ee523a2206206994597C13D831ec7'
export const ETH_DAI_ADDRESS = '0x6B175474E89094C44Da98b954EedeAC495271d0F'

/** Production treasury that receives USDC / USDT / DAI deposits. */
export const ETH_TREASURY_ADDRESS = '0x8eB135581A1c37BA0cA5bCdb43b3c0a9Dc7e5062'
export const ETH_TREASURY_ADDRESS_DEV = '0x8F62DfbF73ea9411b74303b3e1b43ff3Eb64cFD8'

/** Credit-only engine for Ethereum stablecoin → USD deposits. Isolated from eth-v2. */
export const STABLE_ENGINE_ID = 'eth-stable'

/** Dust under this USD amount is recorded and left uncredited. */
export const STABLE_DUST_USD = 1

/** Default fee when a monitor-created row has no precomputed amountReceived (0.25%). */
export const STABLE_MONITOR_FEE_BPS = 25

export const STABLE_TOKENS = {
  USDC: { address: ETH_USDC_ADDRESS, decimals: 6, symbol: 'USDC' },
  USDT: { address: ETH_USDT_ADDRESS, decimals: 6, symbol: 'USDT' },
  DAI: { address: ETH_DAI_ADDRESS, decimals: 18, symbol: 'DAI' },
} as const

export type StableTokenSymbol = keyof typeof STABLE_TOKENS

function requireAddress(name: string): string {
  const value = process.env[name]
  if (!value || !/^0x[a-fA-F0-9]{40}$/.test(value)) {
    throw new Error(`${name} must be a 0x address`)
  }
  return value
}

/** Public address of the inventory wallet. Pair with ETH_SEND_PRIVATE. */
export function getEthSendAddress(): string {
  return requireAddress('ETH_SEND_ADDRESS')
}

/** Public address of the refill wallet. Pair with ETH_SWAP_PRIVATE. */
export function getEthSwapAddress(): string {
  return requireAddress('ETH_SWAP_ADDRESS')
}

/** Treasury that receives stablecoin deposits. Override with ETH_TREASURY_ADDRESS. */
export function getEthTreasuryAddress(): string {
  if (process.env.ETH_TREASURY_ADDRESS && /^0x[a-fA-F0-9]{40}$/.test(process.env.ETH_TREASURY_ADDRESS)) {
    return process.env.ETH_TREASURY_ADDRESS
  }
  if (process.env.NEXT_PUBLIC_ENV === 'development') {
    return ETH_TREASURY_ADDRESS_DEV
  }
  return ETH_TREASURY_ADDRESS
}

export const ENGINE_ID = 'eth-v2'
export const TOKEN_CODE = 'eHEX'

/** Must not be Pending/RetryPending — PulseChain scanBuysForSwap would pick those up. */
export const ETH_SWAP_STATUS = 'Ethereum'

export const ERC20_ABI = [
  'function balanceOf(address owner) view returns (uint256)',
  'function transfer(address to, uint256 amount) returns (bool)',
  'function approve(address spender, uint256 amount) returns (bool)',
  'function allowance(address owner, address spender) view returns (uint256)',
  'function decimals() view returns (uint8)',
  'event Transfer(address indexed from, address indexed to, uint256 value)',
]

export function engineEnabled(): boolean {
  return process.env.ETH_HEX_ENGINE === 'true'
}

/** Stable deposits default on whenever ETH_HEX_ENGINE is on; set ETH_STABLE_ENGINE=false to pause. */
export function stableEngineEnabled(): boolean {
  if (process.env.ETH_STABLE_ENGINE === 'false') return false
  if (process.env.ETH_STABLE_ENGINE === 'true') return true
  return engineEnabled()
}

export function isStableToken(symbol?: string): symbol is StableTokenSymbol {
  return !!symbol && symbol.toUpperCase() in STABLE_TOKENS
}

export function stableTokenByAddress(address: string): typeof STABLE_TOKENS[StableTokenSymbol] | null {
  const lower = address.toLowerCase()
  for (const token of Object.values(STABLE_TOKENS)) {
    if (token.address.toLowerCase() === lower) return token
  }
  return null
}
